import React, {
	useMemo,
	useState,
	useEffect,
	useDeferredValue,
	useCallback,
	useRef,
} from "react";
import "../styling/Publications.css";
import { FixedSizeList } from "react-window";
import { transformManuscript } from "../lib/normalize";
import ManuscriptDetailModal from "./ManuscriptDetailModal";
import { HelpTip } from "./Tooltip";

const CACHE_TTL = 5 * 60 * 1000;
const CACHE_LIMIT = 20;
const publicationKey = (index, data) => data.rows[index].id;

const DEFAULT_DETAIL_ENDPOINT = (id) => `/api/manuscripts/${id}`;

// filter key for manuscripts ticked in this list. It filters the Summary,
// but is never sent with the list's own query, so the list doesn't shrink to the ticked rows.
export const MANUSCRIPT_FILTER_KEY = "manuscriptIds";

const SELECT_HELP_TEXT = "Filter the results in the summary view by the selected manuscripts."

function extractManuscripts(response) {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	if (Array.isArray(response?.manuscripts)) return response.manuscripts;
	if (Array.isArray(response?.data?.manuscripts)) return response.data.manuscripts;
	return [];
}

export default function PublicationsList({
	filters,
	setFilters,
	// if true, rows get a checkbox that adds the manuscript to the filters
	selectable = false,
	manuscripts,
	setPublicationsNr,
	isLoading,
	// backend filtering
	enableBackendFiltering = true,
	backendEndpoint = "/api/analytics/filtered-manuscripts",
	// the component loads its own list from this endpoint.
	listEndpoint,
	detailEndpoint = DEFAULT_DETAIL_ENDPOINT,
	transformDetail = transformManuscript,
	extractList = extractManuscripts,
	hideEmptyDetailFields = false,
	// if true, show a subtle loading indicator while backend filtering is happening
	showBackendLoading = false,
	publicationsCache,
	setPublicationsCache,
	filtersKey,
	showDownloadButton = true,
}) {
	const [searchTerm, setSearchTerm] = useState("");
	const [downloaded, setDownloaded] = useState(false);
	const deferredSearch = useDeferredValue(searchTerm);
	const [backendHadError, setBackendHadError] = useState(false);
	const [internalManuscripts, setInternalManuscripts] = useState([]);
	const [internalLoading, setInternalLoading] = useState(false);
	const [internalHadError, setInternalHadError] = useState(false);
	const [sortConfig, setSortConfig] = useState({ key: "title", direction: "asc" });
	const [columnDirections, setColumnDirections] = useState({
		title: "asc",
		country: "asc",
		year: "asc",
		journal: "asc",
	});

	//query string for the backend filter request, built from all filters except the manuscript selection
	const listQuery = useMemo(() => {
		const params = new URLSearchParams();
		Array.from(filters?.keys() || []).sort().forEach((key) => {
			const values = filters.get(key);
			if (key === MANUSCRIPT_FILTER_KEY) return;
			if (!values || values.size === 0) return;
			Array.from(values).sort().forEach((v) => params.append(key, v));
		});
		return params.toString();
	}, [filters]);

	//determine whether filters are actually applied (manuscript selection does not count)
	const filtersApplied = listQuery !== "";

	const selectedIds = useMemo(
		() => filters?.get(MANUSCRIPT_FILTER_KEY) || new Set(),
		[filters]
	);

	const cacheKey = filtersKey ?? `${backendEndpoint}?${listQuery}`;
	const entry = publicationsCache?.get(cacheKey);
	const cachedEntry = entry && Date.now() - entry.time < CACHE_TTL ? entry : null;

	//backend filtered manuscripts
	const [backendFiltered, setBackendFiltered] = useState(cachedEntry?.data || []);
	const [backendLoading, setBackendLoading] = useState(false);
	const [activeLoading, setActiveLoading] = useState(false);
	const abortRef = useRef(null);

	const hasParentManuscripts = Array.isArray(manuscripts);
	const effectiveManuscripts = hasParentManuscripts ? manuscripts : internalManuscripts;

	//this fetches the list of manuscripts from the backend if no parent list is provided
	useEffect(() => {
		if (hasParentManuscripts || !listEndpoint) return;

		const controller = new AbortController();

		const loadManuscripts = async () => {
			try {
				setInternalLoading(true);
				setInternalHadError(false);

				const response = await fetch(listEndpoint, { signal: controller.signal });
				if (!response.ok) throw new Error("Failed to fetch manuscripts");

				const json = await response.json();
				setInternalManuscripts(extractList(json));
			} catch (error) {
				if (error?.name === "AbortError") return;
				console.error("[MANUSCRIPTS_LIST_ERROR]", error);
				setInternalManuscripts([]);
				setInternalHadError(true);
			} finally {
				if (!controller.signal.aborted) setInternalLoading(false);
			}
		};

		loadManuscripts();
		return () => controller.abort();
	}, [hasParentManuscripts, listEndpoint, extractList]);

	useEffect(() => {
		if (!enableBackendFiltering) return;

		if (!filtersApplied) {
			setBackendFiltered([]);
			setBackendLoading(false);
			setBackendHadError(false);
			return;
		}

		const cachedEntry = publicationsCache?.get(cacheKey);
		if (cachedEntry?.data && Date.now() - cachedEntry.time < CACHE_TTL) {
			setBackendFiltered(cachedEntry.data);
			setBackendHadError(false);
			setBackendLoading(false);
			return;
		}

		const controller = new AbortController();
		abortRef.current?.abort?.();
		abortRef.current = controller;

		const run = async () => {
			try {
				setBackendLoading(true);
				setBackendHadError(false);

				const url = `${backendEndpoint}?${listQuery}`;
				const res = await fetch(url, { signal: controller.signal });

				if (!res.ok) {
					setBackendHadError(true);
					setBackendFiltered([]);
					return;
				}

				const json = await res.json();
				const next = Array.isArray(json?.manuscripts) ? json.manuscripts : [];

				if (controller.signal.aborted) return;
				setBackendFiltered(next);
				setPublicationsCache?.((prev) => {
					const map = new Map(prev);
					map.delete(cacheKey);
					map.set(cacheKey, { data: next, time: Date.now() });
					if (map.size > CACHE_LIMIT) map.delete(map.keys().next().value);
					return map;
				});
			} catch (e) {
				if (e?.name === "AbortError") return;
				console.error("[FILTERED_MANUSCRIPTS_ERROR]", e);
				setBackendHadError(true);
				setBackendFiltered([]);
			} finally {
				if (!controller.signal.aborted) setBackendLoading(false);
			}
		};

		run();
		return () => controller.abort();
	}, [
		enableBackendFiltering,
		backendEndpoint,
		// depends on the query, not on filters, so ticking a manuscript does not refetch the list
		listQuery,
		filtersApplied,
		publicationsCache,
		setPublicationsCache,
		cacheKey,
	]);

	//display manuscripts
	const displayManuscripts = useMemo(() => {
		if (!filtersApplied) return effectiveManuscripts;
		return backendFiltered;
	}, [filtersApplied, effectiveManuscripts, backendFiltered]);

	//sorting
	const sortedManuscripts = useMemo(() => {
		const sorted = [...(displayManuscripts || [])];
		sorted.sort((a, b) => {
			let aVal = a[sortConfig.key === "year" ? "publication_year" : sortConfig.key];
			let bVal = b[sortConfig.key === "year" ? "publication_year" : sortConfig.key];
			if (sortConfig.key === "year") {
				aVal = aVal == null ? -Infinity : Number(aVal);
				bVal = bVal == null ? -Infinity : Number(bVal);
				return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
			}
			//ignores first trailing characters that are not letters or numbs
			const cmp = String(aVal ?? "").replace(/^[^\p{L}\p{N}]+/u, "").localeCompare(
				String(bVal ?? "").replace(/^[^\p{L}\p{N}]+/u, "")
			);
			return sortConfig.direction === "asc" ? cmp : -cmp;
		});
		return sorted;
	}, [displayManuscripts, sortConfig]);

	//search
	const rows = useMemo(() => {
		const term = (deferredSearch || "").trim().toLowerCase();
		if (!term) return sortedManuscripts;
		return sortedManuscripts.filter(
			(m) =>
				(m.title || "").toLowerCase().includes(term) ||
				(m.journal || "").toLowerCase().includes(term) ||
				(m.country || "").toLowerCase().includes(term)
		);
	}, [sortedManuscripts, deferredSearch]);

	useEffect(() => {
		if (!isLoading && !internalLoading && effectiveManuscripts.length > 0) {
			setPublicationsNr?.(rows.length);
		}
	}, [rows.length, setPublicationsNr, isLoading, internalLoading, effectiveManuscripts.length]);

	//modal
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(null);

	const onRowClick = useCallback(async (m) => {
		setActive(null);
		setActiveLoading(true);
		setOpen(true);
		try {
			const res = await fetch(detailEndpoint(m.id));
			if (!res.ok) throw new Error("Failed to fetch manuscript");
			const json = await res.json();
			setActive(transformDetail(json?.data?.manuscript ?? json?.manuscript ?? json));
		} catch (e) {
			console.error("[MANUSCRIPT_FETCH_ERROR]", e);
			setActive(null);
		} finally {
			setActiveLoading(false);
		}
	}, [detailEndpoint, transformDetail]);

	//sort handler
	const handleSort = useCallback(
		(key) => {
			const currentDirection = columnDirections[key] || "asc";
			const newDirection = currentDirection === "asc" ? "desc" : "asc";

			setColumnDirections((prev) => ({ ...prev, [key]: newDirection }));
			setSortConfig({ key, direction: newDirection });
		},
		[columnDirections]
	);

	//this will download a tsv file of the currently visible rows with columns: title, country, year, doi
	const downloadTsv = useCallback(() => {
		const header = ["title", "country", "year", "doi"].join("\t");
		const escape = (val) => {
			const s = String(val ?? "—");
			return s.includes("\t") || s.includes("\n") || s.includes('"')
				? `"${s.replace(/"/g, '""')}"`
				: s;
		};
		const body = rows
			.map((m) => [m.title, m.country, m.publication_year, m.doi].map(escape).join("\t"))
			.join("\n");

		const blob = new Blob([`${header}\n${body}`], { type: "text/tab-separated-values" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `PROCare_references_${rows.length}pubs.tsv`;
		a.click();
		URL.revokeObjectURL(url);
		setDownloaded(true);
		setTimeout(() => setDownloaded(false), 5000);
	}, [rows]);

	const SortIndicator = ({ columnKey }) => {
		if (sortConfig.key !== columnKey) return null;
		return <span style={{ marginLeft: "4px" }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>;
	};

	//tick / untick a manuscript in the filters, same pattern as clicking a value in a facet
	const toggleSelect = useCallback((id) => {
		setFilters?.((prev) => {
			const next = new Map(prev);
			const curr = new Set(next.get(MANUSCRIPT_FILTER_KEY) || []);
			if (curr.has(id)) curr.delete(id);
			else curr.add(id);
			next.set(MANUSCRIPT_FILTER_KEY, curr);
			return next;
		});
	}, [setFilters]);

	//row renderer
	const itemData = useMemo(
		() => ({ rows, onRowClick, selectable, selectedIds, toggleSelect }),
		[rows, onRowClick, selectable, selectedIds, toggleSelect]
	);

	const PublicationRow = useCallback(({ index, style, data }) => {
		const m = data.rows[index];
		const id = String(m.id);
		const isSelected = data.selectable && data.selectedIds.has(id);
		return (
			<div style={style}>
				<button
					className={`pubs-row pubs-grid${data.selectable ? " selectable" : ""}${isSelected ? " selected" : ""}`}
					onClick={() => data.onRowClick(m)}
					style={{ height: "100%", width: "100%" }}
				>
					{data.selectable && (
						<span
							className="pubs-check-cell"
							title={isSelected ? "Remove from Summary filter" : "Show this publication's data in Summary"}
							onClick={(e) => {
								e.stopPropagation();
								data.toggleSelect(id);
							}}
						>
							<span
								role="checkbox"
								aria-checked={isSelected}
								aria-label="Select manuscript"
								className={`pubs-check${isSelected ? " checked" : ""}`}
							/>
						</span>
					)}
					<div className="pubs-title" title={m.title}>{m.title || "—"}</div>
					<div>{m.country || "UNK"}</div>
					<div>{m.publication_year?.substr(0, 4) || "—"}</div>
					<div title={m.journal}>{m.journal || "—"}</div>
					<div className="pubs-col-link">
						{m.doi && (
							<a
								href={m.doi}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(e) => e.stopPropagation()}
								className="pubs-link"
								title="Open publication"
							>
								🔗
							</a>
						)}
					</div>
				</button>
			</div>
		);
	}, []);

	const bodyRef = useRef(null);
	const listRef = useRef(null);
	const scrollOffset = useRef(0);
	const [listHeight, setListHeight] = useState(200);
	useEffect(() => {
		const node = bodyRef.current;
		if (!node) return;
		const observer = new ResizeObserver(([entry]) => {
			const height = Math.floor(entry.contentRect.height);
			if (height > 0) setListHeight(height);
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		scrollOffset.current = 0;
		listRef.current?.scrollTo(0);
	}, [deferredSearch, sortConfig, listQuery]);

	const showLoadingState = Boolean(isLoading) || internalLoading || backendLoading;
	const hadLoadingError = internalHadError || backendHadError;

	return (
		<div className="pubs">
			<div className="pubs-head">
				<div className="pubs-count">
					{searchTerm && sortedManuscripts.length !== rows.length && (
						<span className="search-hint"> (filtered from {sortedManuscripts.length})</span>
					)}
					{showBackendLoading && backendLoading && (
						<span className="search-hint"> (updating…)</span>
					)}
					{selectable && selectedIds.size > 0 && (
						<span className="pubs-selected-count">
							{selectedIds.size} selected
						</span>
					)}
				</div>

				<span className="pubs-controls">
					<div className="search-bar">
						<svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path
								d="M7 12.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11ZM13.5 13.5l-3-3"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>

						<input
							type="text"
							className="search-input"
							placeholder="Search publications..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>

						{searchTerm && (
							<button className="search-clear" onClick={() => setSearchTerm("")} title="Clear search">
								✕
							</button>
						)}
					</div>

					{showDownloadButton && (
						<button
							className="chip"
							onClick={downloadTsv}
							disabled={rows.length === 0 || downloaded}
							title={`Download ${rows.length} 
									visible reference${rows.length !== 1 ? "s" : ""} as TSV`}
						>
							{downloaded ? "✓ Downloaded!" : "↓ Download references"}
						</button>
					)}

				</span>
			</div>

			<div className="pubs-table">
				<div className={`pubs-header pubs-grid${selectable ? " selectable" : ""}`}>
					{selectable && (
						<div className="pubs-filter-head">
							Filter <HelpTip text={SELECT_HELP_TEXT} />
						</div>
					)}
					<div onClick={() => handleSort("title")} style={{ cursor: "pointer", marginLeft: "8px", userSelect: "none" }}>
						Title <SortIndicator columnKey="title" />
					</div>

					<div onClick={() => handleSort("country")} style={{ cursor: "pointer", userSelect: "none" }}>
						Country <SortIndicator columnKey="country" />
					</div>

					<div onClick={() => handleSort("year")} style={{ cursor: "pointer", userSelect: "none" }}>
						Year <SortIndicator columnKey="year" />
					</div>

					<div onClick={() => handleSort("journal")} style={{ cursor: "pointer", userSelect: "none" }}>
						Journal <SortIndicator columnKey="journal" />
					</div>

					<div className="pubs-col-link">Link</div>
				</div>

				<div className="pubs-body" ref={bodyRef}>
					{showLoadingState ? (
						<div className="pubs-loading">
							<div className="spinner"></div>
							<div className="loading-text">Loading...</div>
						</div>
					) : hadLoadingError ? (
						<div className="pubs-empty">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
								<circle cx="12" cy="12" r="10" />
								<path d="M12 8v4" />
								<circle cx="12" cy="16" r="0.5" fill="#DC2626" />
							</svg>
							<div className="empty-title" style={{ color: "#DC2626" }}>
								Failed to load publications
							</div>
						</div>
					) : rows.length === 0 ? (
						<div className="pubs-empty">
							<div className="empty-title">No publications found</div>
						</div>
					) : (
						<FixedSizeList
							ref={listRef}
							className="pubs-list"
							height={listHeight}
							width="100%"
							itemCount={rows.length}
							itemSize={38}
							itemData={itemData}
							itemKey={publicationKey}
							overscanCount={5}
							initialScrollOffset={scrollOffset.current}
							onScroll={({ scrollOffset: offset }) => { scrollOffset.current = offset; }}
						>
							{PublicationRow}
						</FixedSizeList>
					)}
				</div>
			</div>

			<ManuscriptDetailModal
				open={open}
				onClose={() => setOpen(false)}
				manuscript={active}
				loading={activeLoading}
				hideEmptyFields={hideEmptyDetailFields}
			/>
		</div>
	);
}